// The tooth chart and the treatment plan, as the visit form, the patient page
// and the printed statement all lay them out. Keep in step with
// backend/utils/dentalChart.js and backend/utils/dentalPlan.js.

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, index) => from + index);

// A row of the chart as the dentist sees the patient: the patient's right is
// on the left of the screen. `right` and `left` are FDI quadrant numbers.
const row = (right, left, count) => [
    range(1, count).reverse().map((tooth) => `${right}${tooth}`),
    range(1, count).map((tooth) => `${left}${tooth}`)
];

// [upper, lower], each [patient's right, patient's left].
export const CHARTS = {
    permanent: [row(1, 2, 8), row(4, 3, 8)],
    primary: [row(5, 6, 5), row(8, 7, 5)]
};

export const isPrimaryTooth = (tooth) => /^[5-8]/.test(String(tooth || ''));

// [condition, translation key, fallback, colour]. The colours are chosen to be
// told apart at the size of a tooth on the chart.
export const CONDITIONS = [
    ['healthy', 'toothHealthy', 'Healthy', '#52c41a'],
    ['caries', 'toothCaries', 'Caries', '#f5222d'],
    ['filling', 'toothFilling', 'Filling', '#1677ff'],
    ['rootCanal', 'toothRootCanal', 'Root canal', '#722ed1'],
    ['crown', 'toothCrown', 'Crown', '#faad14'],
    ['bridge', 'toothBridge', 'Bridge', '#d48806'],
    ['implant', 'toothImplant', 'Implant', '#13c2c2'],
    ['veneer', 'toothVeneer', 'Veneer', '#eb2f96'],
    ['fracture', 'toothFracture', 'Fracture', '#fa541c'],
    ['mobile', 'toothMobile', 'Mobile', '#a0d911'],
    ['impacted', 'toothImpacted', 'Impacted', '#2f54eb'],
    ['toExtract', 'toothToExtract', 'To extract', '#820014'],
    ['extracted', 'toothExtracted', 'Extracted', '#595959'],
    ['missing', 'toothMissing', 'Missing', '#bfbfbf']
];

const CONDITION_BY_KEY = new Map(CONDITIONS.map((entry) => [entry[0], entry]));

// A tooth that is no longer in the mouth is drawn struck through.
export const isGone = (condition) => condition === 'extracted' || condition === 'missing';

// [letter, translation key, fallback].
export const SURFACES = [
    ['M', 'surfaceMesial', 'Mesial'],
    ['O', 'surfaceOcclusal', 'Occlusal'],
    ['D', 'surfaceDistal', 'Distal'],
    ['B', 'surfaceBuccal', 'Buccal'],
    ['L', 'surfaceLingual', 'Lingual']
];

// i18next hands back the key itself when a string is missing, so `t(key) ||
// fallback` never reaches the fallback.
export const translate = (t, key, fallback) => {
    const value = t ? t(key) : null;
    return value && value !== key ? value : fallback;
};

export const conditionLabel = (t, condition) => {
    const entry = CONDITION_BY_KEY.get(condition);
    return entry ? translate(t, entry[1], entry[2]) : '';
};

export const conditionColor = (condition) => (CONDITION_BY_KEY.get(condition) || [])[3];

// One line about a tooth: "16 - Caries (M, O) - deep".
export const describeTooth = (t, entry) => [
    entry.tooth,
    conditionLabel(t, entry.condition),
    entry.surfaces?.length > 0 ? `(${entry.surfaces.join(', ')})` : '',
    entry.note
].filter(Boolean).join(' - ').replace(' - (', ' (');

export const hasChart = (dental) => Array.isArray(dental?.chart) && dental.chart.length > 0;

// ---- the treatment plan ---------------------------------------------------------

// [status, translation key, fallback, colour].
export const ITEM_STATUSES = [
    ['planned', 'dentalPlanned', 'Planned', 'default'],
    ['in_progress', 'dentalInProgress', 'In progress', 'blue'],
    ['done', 'dentalDone', 'Done', 'green'],
    ['cancelled', 'dentalCancelled', 'Cancelled', 'red']
];

export const PLAN_STATUSES = [
    ['active', 'dentalPlanActive', 'In progress', 'blue'],
    ['completed', 'dentalPlanCompleted', 'Completed', 'green'],
    ['cancelled', 'dentalCancelled', 'Cancelled', 'red']
];

export const INSTALLMENT_STATUSES = [
    ['paid', 'installmentPaid', 'Paid', 'green'],
    ['partial', 'installmentPartial', 'Part paid', 'gold'],
    ['due', 'installmentDue', 'Due', 'blue'],
    ['overdue', 'installmentOverdue', 'Overdue', 'red']
];

// A plan seen as an invoice: what the patient still has to be asked for.
export const INVOICE_STATUSES = [
    ['unpaid', 'invoiceUnpaid', 'Unpaid', 'red'],
    ['partial', 'invoicePartial', 'Part paid', 'gold'],
    ['paid', 'invoicePaid', 'Paid', 'green'],
    ['cancelled', 'dentalCancelled', 'Cancelled', 'default']
];

// [method, translation key, fallback].
export const PAYMENT_METHODS = [
    ['cash', 'cash', 'Cash'],
    ['card', 'card', 'Card'],
    ['insurance', 'insurance', 'Insurance']
];

const lookup = (list) => {
    const byKey = new Map(list.map((entry) => [entry[0], entry]));
    return {
        label: (t, key) => {
            const entry = byKey.get(key);
            return entry ? translate(t, entry[1], entry[2]) : key;
        },
        color: (key) => (byKey.get(key) || [])[3]
    };
};

export const itemStatus = lookup(ITEM_STATUSES);
export const planStatus = lookup(PLAN_STATUSES);
export const installmentStatus = lookup(INSTALLMENT_STATUSES);
export const invoiceStatus = lookup(INVOICE_STATUSES);
export const paymentMethod = lookup(PAYMENT_METHODS);

// Whole dinars, never negative - what the server will store.
export const money = (value) => {
    const number = Math.round(Number(value));
    return Number.isFinite(number) && number > 0 ? number : 0;
};

// "1,250,000 IQD". Written with Latin digits in every language, as the
// receipts are.
export const formatMoney = (value, currency = 'IQD') => `${money(value).toLocaleString('en-US')} ${currency}`;

// What the plan form shows while it is being written, before the server has
// worked out the real totals.
export const draftTotals = (items = [], discount = 0) => {
    const subtotal = (items || [])
        .filter((item) => item && item.status !== 'cancelled')
        .reduce((sum, item) => sum + money(item.price), 0);
    const off = Math.min(money(discount), subtotal);
    return { subtotal, discount: off, total: subtotal - off };
};
